import React from 'react';

const GenesysPreviewModal = ({
  open,
  onClose,
  onConfirm,
  previewData,
  loading,
  error
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6">
        <h3 className="text-lg font-semibold mb-4">Preview Recording Download</h3>

        {loading && (
          <div className="flex flex-col items-center justify-center py-8">
            <svg className="animate-spin h-10 w-10 text-cyan-600 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-gray-600">Querying Genesys for recordings...</p>
          </div>
        )}

        {error && !loading && (
          <div className="mb-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <svg className="h-5 w-5 text-red-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-red-800">Preview Failed</h4>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={onClose} className="btn-outline">
                Close
              </button>
            </div>
          </div>
        )}

        {previewData && !loading && !error && (
          <>
            <div className="mb-6 space-y-4">
              {/* Summary Stats */}
              <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-200">
                <div>
                  <p className="text-sm font-medium text-gray-600">Conversations</p>
                  <p className="text-2xl font-bold text-cyan-700">{previewData.conversationCount || 0}</p>
                </div>
              </div>

              {/* Warning if estimate */}
              {previewData.isEstimate && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex">
                    <svg className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-xs font-medium text-yellow-800">Partial Count</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        This is a preview count limited to the first 1000 conversations. Actual recording count may be higher.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* No conversations found */}
              {!previewData.conversationCount && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <svg className="h-12 w-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-sm font-medium text-gray-700">No conversations found</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Try adjusting your date range or filters
                  </p>
                </div>
              )}

              {/* Confirmation message */}
              {previewData.conversationCount > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Ready to proceed?</strong> This will start downloading recordings from{' '}
                    <span className="font-bold">{previewData.conversationCount}</span> conversation(s).
                  </p>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex justify-end space-x-2">
              <button onClick={onClose} className="btn-outline">
                Cancel
              </button>
              {previewData.conversationCount > 0 && (
                <button
                  onClick={() => {
                    onConfirm();
                  }}
                  className="btn-success"
                >
                  Confirm & Download
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GenesysPreviewModal;
